import { Box, Card, Stack, Chip, Typography } from '@atgfr8/atg-ui-tailwind';
import { FileUploadOutlineIcon, InformationIcon } from '@atgfr8/icons';
import { Button } from '@components/Button';
import type { UploadPage } from '@typeDefinitions/uploadToolTypes';
import * as pdfjsLib from 'pdfjs-dist';
import pdfjsWorker from 'pdfjs-dist/build/pdf.worker?url';
import { Accessor, For, Setter, Show, createSignal } from 'solid-js';

import { ImagePage } from './components/ImagePage';
import { PdfPage } from './components/PdfPage';
import { PDFPageSkeleton } from './components/Skeleton';

pdfjsLib.GlobalWorkerOptions.workerSrc = pdfjsWorker;

type DocumentUploadProps = {
  pages: UploadPage[];
  onFileUpload: (files: FileList) => void;
  selected: Accessor<string[]>;
  setSelected: Setter<string[]>;
  loading: Accessor<boolean>;
  onDropPage: (origin: string, pageId: string[], targetZone: string) => void;
};

export const DocumentUpload = (props: DocumentUploadProps) => {
  let fileInputRef: HTMLInputElement | undefined;

  const [isDragOver, setIsDragOver] = createSignal(false);

  const handleDrop = (event: DragEvent) => {
    event.preventDefault();
    setIsDragOver(false);
    const dt = event.dataTransfer;
    if (!dt) return;

    const origin = dt.getData('origin');
    const rawIds = dt.getData('pageIds');

    if (origin && rawIds) {
      let pageIds: string[] = [];
      try {
        const parsed = JSON.parse(rawIds);
        if (Array.isArray(parsed)) pageIds = parsed;
      } catch {
        pageIds = [rawIds];
      }
      props.onDropPage(origin, pageIds, 'document');
    } else if (dt.files.length > 0) {
      void props.onFileUpload(dt.files);
    }
  };

  const handleDragOver = (event: DragEvent) => {
    event.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = () => {
    setIsDragOver(false);
  };

  return (
    <Card class="max-h-[800px] flex flex-col rounded-xl bg-[rgba(242,246,248,1)]! mb-2">
      <Box class="bg-[rgba(236,239,241,1)] h-12 px-4 flex items-center justify-between shrink-0">
        <Typography variant="subtitle2" class="text-base!">
          Document Pages
          <Show when={props.pages.length > 0}>
            <Chip
              id="document-pages-count"
              text={String(props.pages.length)}
              class="mx-2 bg-[#123b50]! text-white!"
            />
          </Show>
        </Typography>
        <Stack direction="row" alignItems="center" spacing={1}>
          <Show when={props.selected().length}>
            <Button
              label="Deselect"
              variant="outlined"
              size="sm"
              onClick={() => props.setSelected([])}
              dataTestId="document-upload-deselect-button"
            />
          </Show>
          <Typography class="flex items-center gap-1 text-sm! text-[#333]">
            <InformationIcon /> Hold Ctrl to select multiple pages
          </Typography>
        </Stack>
      </Box>

      <Box class="flex-1 overflow-y-auto p-2" style={{ 'min-height': '0px' }}>
        <Box
          class="flex flex-wrap gap-4 justify-start"
          on:drop={handleDrop}
          on:dragover={handleDragOver}
          on:dragleave={handleDragLeave}
        >
          <Show when={props.loading()}>
            <PDFPageSkeleton />
          </Show>

          <Show when={!props.loading() && props.pages.length > 0}>
            <For each={props.pages}>
              {(uploadPage) => {
                const isSelected = () =>
                  props.selected().includes(uploadPage.id);

                const onClick = (e: MouseEvent) => {
                  if (e.ctrlKey || e.metaKey) {
                    props.setSelected((prev) =>
                      isSelected()
                        ? prev.filter((id) => id !== uploadPage.id)
                        : [...prev, uploadPage.id],
                    );
                  } else {
                    e.preventDefault();
                  }
                };

                return (
                  <div
                    draggable="true"
                    onDragStart={(e) => {
                      const selectedIds = props.selected();
                      const idsToDrag = selectedIds.includes(uploadPage.id)
                        ? selectedIds
                        : [uploadPage.id];

                      e.dataTransfer?.setData('origin', 'document');
                      e.dataTransfer?.setData(
                        'pageIds',
                        JSON.stringify(idsToDrag),
                      );
                      e.dataTransfer!.effectAllowed = 'move';
                    }}
                    class="rounded-sm shadow-[2px_2px_4px_#CCC]"
                    style={{
                      border: isSelected()
                        ? '2px solid #1B4960'
                        : '1px solid #c3c3c3',
                      cursor: 'pointer',
                    }}
                    onClick={onClick}
                  >
                    {typeof uploadPage.page === 'string' ? (
                      <ImagePage
                        imageUrl={uploadPage.page}
                        width={260}
                        height={320}
                      />
                    ) : (
                      <PdfPage
                        page={uploadPage.page}
                        width={260}
                        height={320}
                      />
                    )}
                  </div>
                );
              }}
            </For>
          </Show>

          <Show when={!props.loading() && props.pages.length === 0}>
            <Box
              class="h-[400px] w-full border-2 border-dashed border-gray-300 rounded-lg flex flex-col gap-4 items-center justify-center transition"
              on:drop={handleDrop}
              on:dragover={handleDragOver}
              on:dragleave={handleDragLeave}
              onClick={() => fileInputRef?.click()}
              style={{
                'background-color': isDragOver() ? '#A9E5AF' : '#F2F6F8',
              }}
            >
              <FileUploadOutlineIcon />
              <Typography variant="subtitle1" class="text-gray-500 text-[16px]">
                <span class="underline cursor-pointer mx-1">
                  Click to upload
                </span>
                or drag and drop
              </Typography>
              <input
                type="file"
                multiple
                ref={(el) => (fileInputRef = el)}
                style={{ display: 'none' }}
                accept="application/pdf,image/png,image/jpeg"
                onChange={(e) => {
                  const files = e.currentTarget.files;
                  if (files) void props.onFileUpload(files);
                }}
              />
            </Box>
          </Show>
        </Box>
      </Box>
    </Card>
  );
};
